// A semana de horas da pessoa — a tela onde o escritório anota e confere.
//
// Desde 30/09/2026 a semana é SÓ o calendário (7 dias nas colunas, horas nas
// linhas). A grade de "linha por contrato × coluna por dia" saiu a pedido do
// Lucas — com o calendário e o cronômetro ela virou um segundo jeito de fazer a
// mesma coisa, e 92% dos lançamentos desde 23/09 já tinham horário. O que só a
// grade fazia mudou de lugar: marcar ausência (e desfazê-la) fica no cabeçalho
// de cada dia do calendário — "fechar o dia" deixou de existir em 01/10/2026; o total da semana e o total por contrato
// ficam no resumo abaixo dele; lançar só a quantidade (sem horário) continua
// pela janela "Lançar horas" e aparece na faixa "sem horário".
//
// Esta tela também é a semana de OUTRA pessoa (aba Equipe → clicar num nome,
// e a Ficha do Profissional): `emailAlvo` vai em cada pedido e o servidor
// exige horas.ver_equipe para ler e horas.lancar_por_outro para escrever.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, App as AntApp, Button, Space, Tag, Typography } from 'antd';
import { LeftOutlined, RightOutlined, EditOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import {
  api, agoraSP, aplicarMarcaLocal, chaveDestino, corDoDestino, diaDaSemana, formatarHoras, horarioParaMin,
  segundaDe, type AtividadeTipo, type CentroCustoOpc, type ContratoOpc, type MarcaLocal, type Semana,
} from './api';
import TotalHoras from './TotalHoras';
import { segundaDeIso } from './horasUtils';
import { EVENTO_HORAS } from '../../components/HorasAtalho';
import CalendarioSemana from './CalendarioSemana';
import ModalApontamento, { rascunhoDe, type RascunhoApontamento } from './ModalApontamento';

const { Text, Title } = Typography;

export default function MinhaSemana({ emailAlvo }: { emailAlvo?: string }) {
  const { message } = AntApp.useApp();
  const [inicio, setInicio] = useState<Dayjs>(() => segundaDe(dayjs()));
  const [dados, setDados] = useState<Semana | null>(null);
  const [recebidoEm, setRecebidoEm] = useState(() => Date.now());
  const [contratos, setContratos] = useState<ContratoOpc[]>([]);
  const [ccs, setCcs] = useState<CentroCustoOpc[]>([]);
  const [catalogo, setCatalogo] = useState<AtividadeTipo[]>([]);
  const [rascunho, setRascunho] = useState<RascunhoApontamento | null>(null);
  const [, setTique] = useState(0);

  const qs = emailAlvo ? `&email=${encodeURIComponent(emailAlvo)}` : '';

  // A semana ABRE pelo "hoje" do servidor (SP): o relógio do computador só
  // serve para o primeiro pedido. Fora de SP, domingo à noite local já pode
  // ser segunda em SP — sem isto, "Lançar horas" caía na semana passada.
  const navegou = useRef(false);
  const carregar = useCallback(async () => {
    try {
      const d = await api(`/financeiro/horas/semana?inicio=${inicio.format('YYYY-MM-DD')}${qs}`);
      if (!navegou.current && d.hoje && segundaDeIso(d.hoje) !== inicio.format('YYYY-MM-DD')) {
        navegou.current = true;
        setInicio(dayjs(segundaDeIso(d.hoje)));
        return;
      }
      setDados(d);
      setRecebidoEm(Date.now());
    } catch (e: any) { message.error(e.message); }
  }, [inicio, qs, message]);

  useEffect(() => { carregar(); }, [carregar]);

  // O atalho do cabeçalho avisa quando lança ou mexe no cronômetro; e a aba
  // relê ao voltar o foco (outra aba pode ter mexido). Sem isto o calendário
  // mostrava um cronômetro que já tinha parado.
  useEffect(() => {
    const reler = () => { if (document.visibilityState === 'visible') carregar(); };
    window.addEventListener(EVENTO_HORAS, carregar);
    document.addEventListener('visibilitychange', reler);
    return () => {
      window.removeEventListener(EVENTO_HORAS, carregar);
      document.removeEventListener('visibilitychange', reler);
    };
  }, [carregar]);
  const avisarAtalho = () => { try { window.dispatchEvent(new Event(EVENTO_HORAS)); } catch { /* sem window */ } };

  // A linha do "agora" e o bloco do cronômetro andam sozinhos, de minuto em
  // minuto, sem ir ao servidor.
  useEffect(() => {
    const t = setInterval(() => setTique(x => x + 1), 60 * 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const d = await api('/financeiro/horas/contratos');
        setContratos(d.contratos || []);
        setCcs(d.centros_custo || []);
      } catch (e: any) { message.error(e.message); }
    })();
    (async () => {
      // Lista de atividades: sem ela o campo continua aceitando texto livre.
      try { setCatalogo((await api('/financeiro/horas/tipos-atividade')).atividades || []); }
      catch { setCatalogo([]); }
    })();
  }, [message]);

  const agora = dados && typeof dados.agora_minuto === 'number'
    ? agoraSP(dados.hoje, dados.agora_minuto, recebidoEm, Date.now()) : null;

  const datas = useMemo(
    () => Array.from({ length: 7 }, (_, i) => inicio.add(i, 'day').format('YYYY-MM-DD')),
    [inicio]);

  // "Lançar horas": no dia de hoje, se hoje está na semana aberta.
  function novoLancamento(extra: Partial<RascunhoApontamento> = {}) {
    const d = dados && datas.includes(dados.hoje) ? dados.hoje : datas[0];
    setRascunho({ data: d, ...extra });
  }

  // Minutos do cronômetro ligado que caem num dia (do início até agora).
  const cronometroNoDia = (data: string): number => {
    const cr = dados?.cronometro;
    if (!cr || !agora || data < cr.data_inicio || data > agora.data) return 0;
    const ini = data === cr.data_inicio ? (horarioParaMin(cr.hora_inicio) ?? 0) : 0;
    const fim = data === agora.data ? agora.minuto : 1440;
    return Math.max(0, fim - ini);
  };

  const diaPorData = useMemo(() => {
    const m = new Map<string, Semana['dias'][number]>();
    (dados?.dias || []).forEach(d => m.set(d.data, d));
    return m;
  }, [dados]);

  // Resumo da semana: total e quanto foi para cada contrato / centro de custo.
  // É o que a coluna "Total" da grade antiga mostrava — aqui, numa linha.
  const porDestino = useMemo(() => {
    const mapa = new Map<string, { chave: string; rotulo: string; total: number }>();
    (dados?.apontamentos || []).forEach(a => {
      const chave = chaveDestino(a);
      const atual = mapa.get(chave) || { chave, rotulo: a.contrato_numero || a.centro_custo_nome || '(sem destino)', total: 0 };
      atual.total += a.minutos;
      mapa.set(chave, atual);
    });
    // Array.from, não [...mapa.values()]: o tsconfig deste projeto não liga
    // downlevelIteration, e espalhar iterador não compila.
    return Array.from(mapa.values()).sort((a, b) => b.total - a.total);
  }, [dados]);
  const totalSemana = porDestino.reduce((s, d) => s + d.total, 0);

  async function acaoDia(data: string, acao: 'reabrir' | 'ausencia', tipo?: string) {
    try {
      await api(`/financeiro/horas/dia/${data}/${acao}`, {
        method: 'POST',
        body: JSON.stringify({
          ...(tipo ? { tipo_ausencia: tipo } : {}),
          ...(emailAlvo ? { usuario_email: emailAlvo } : {}),
        }),
      });
      await carregar();
      avisarAtalho();
      message.success(acao === 'reabrir' ? 'Ausência desfeita' : 'Ausência registrada');
    } catch (e: any) { message.error(e.message); }
  }

  // Home office / campo do dia. A caixa responde na hora (o servidor confirma
  // depois); se ele recusar, a releitura desfaz. Só relê quando o ÚLTIMO
  // clique em voo volta — reler no meio de dois cliques seguidos faria a
  // segunda caixa piscar desmarcada.
  const marcasEmVoo = useRef(0);
  async function marcarLocal(data: string, marca: MarcaLocal, valor: boolean) {
    setDados(d => (d ? { ...d, dias: aplicarMarcaLocal(d.dias || [], data, marca, valor) } : d));
    marcasEmVoo.current += 1;
    let falhou = false;
    try {
      await api(`/financeiro/horas/dia/${data}/local`, {
        method: 'POST',
        body: JSON.stringify({ [marca]: valor, ...(emailAlvo ? { usuario_email: emailAlvo } : {}) }),
      });
    } catch (e: any) { falhou = true; message.error(e.message); }
    finally {
      marcasEmVoo.current -= 1;
      if (marcasEmVoo.current === 0) {
        if (falhou || emailAlvo) await carregar();
        else avisarAtalho();   // o atalho e esta tela releem pelo mesmo aviso
      }
    }
  }

  const cron = dados?.cronometro || null;
  const onde = (x: { contrato_numero: string | null; centro_custo_nome: string | null }) =>
    x.contrato_numero || x.centro_custo_nome || '—';

  return (
    <div>
      <Space wrap style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }}>
        <Space wrap>
          <Button icon={<LeftOutlined />} onClick={() => { navegou.current = true; setInicio(i => i.subtract(7, 'day')); }} />
          <Title level={5} style={{ margin: 0, minWidth: 210, textAlign: 'center' }}>
            {inicio.format('DD/MM')} a {inicio.add(6, 'day').format('DD/MM/YYYY')}
          </Title>
          <Button icon={<RightOutlined />} onClick={() => { navegou.current = true; setInicio(i => i.add(7, 'day')); }} />
          <Button onClick={() => { navegou.current = true; setInicio(dados?.hoje ? dayjs(segundaDeIso(dados.hoje)) : segundaDe(dayjs())); }}>Esta semana</Button>
        </Space>
        <Button type="primary" icon={<EditOutlined />} onClick={() => novoLancamento()}>
          Lançar horas
        </Button>
      </Space>

      {dados?.sem_cadastro_profissional && (
        <Alert
          type="warning" showIcon style={{ marginBottom: 12 }}
          message="Suas horas estão sendo gravadas, mas ainda não têm custo"
          description="Seu login não está ligado a um cadastro de profissional, então estas horas não entram no custo dos contratos. Peça para a administração fazer essa ligação — o que você anotar até lá não se perde."
        />
      )}

      {/* O cronômetro se controla pelo relógio do cabeçalho; aqui só se avisa.
          Na semana de OUTRA pessoa ele é só leitura — as rotas do cronômetro
          agem apenas no próprio usuário. */}
      {cron && (
        <Alert
          type="info" showIcon style={{ marginBottom: 12 }}
          message={emailAlvo
            ? `Esta pessoa está com o cronômetro ligado desde ${diaDaSemana(cron.data_inicio)} ${cron.data_inicio.slice(8, 10)}/${cron.data_inicio.slice(5, 7)} às ${cron.hora_inicio} em ${onde(cron)}.`
            : `Cronômetro ligado desde ${cron.hora_inicio}${cron.data_inicio !== dados?.hoje ? ` de ${cron.data_inicio.slice(8, 10)}/${cron.data_inicio.slice(5, 7)}` : ''} em ${onde(cron)}. Pare ou troque pelo relógio no topo da tela.`}
        />
      )}

      <CalendarioSemana
        datas={datas}
        apontamentos={dados?.apontamentos || []}
        dias={diaPorData}
        hoje={dados?.hoje || dayjs().format('YYYY-MM-DD')}
        agora={agora}
        cronometro={cron}
        cronometroDeOutro={!!emailAlvo}
        onCriar={(data, ini, fim) => novoLancamento({
          data,
          hora_inicio: `${String(Math.floor(ini / 60)).padStart(2, '0')}:${String(ini % 60).padStart(2, '0')}`,
          hora_fim: `${String(Math.floor(fim / 60)).padStart(2, '0')}:${String(fim % 60).padStart(2, '0')}`,
        })}
        onEditar={(a) => setRascunho(rascunhoDe(a))}
        onLocal={marcarLocal}
        onAcaoDia={acaoDia}
      />

      {/* Resumo da semana: o total e o quanto foi para cada lugar, na cor do calendário. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 10 }}>
        <Text strong>Semana:</Text>
        <TotalHoras minutos={totalSemana} forte alinhar="start" />
        {porDestino.map(d => {
          const cor = corDoDestino(d.chave);
          return (
            <Tag key={d.chave} style={{ marginInline: 0, background: cor.fundo, borderColor: cor.borda, color: '#222' }}>
              <strong>{d.rotulo}</strong> · {formatarHoras(d.total)}
            </Tag>
          );
        })}
        {!porDestino.length && dados && <Text type="secondary">nada lançado nesta semana</Text>}
      </div>

      <ModalApontamento
        aberto={!!rascunho} inicial={rascunho}
        contratos={contratos} ccs={ccs} catalogo={catalogo}
        emailAlvo={emailAlvo}
        hoje={dados?.hoje || dayjs().format('YYYY-MM-DD')}
        horasDia={dados?.horas_dia || 8}
        apontamentosDaSemana={dados?.apontamentos || []}
        cronometroNoDia={cronometroNoDia}
        onFechar={() => setRascunho(null)}
        onSalvo={async (aviso) => {
          setRascunho(null);
          if (aviso) message.success(aviso);
          await carregar();
          avisarAtalho();
        }}
      />
    </div>
  );
}
